<script>
  import {mapGetters} from 'vuex'
  export default {
    name: 't-main-item',
    props: {
      type: {
        type: String,
        default: 'simple',
        validator: v => ['simple', 'card', 'card full'].indexOf(v) !== -1
      },
      to: {
        type: String
      },
      href: {
        type: String
      },
      text: {
        type: String
      },
      textInOneLine: {
        type: Boolean
      },
      simpleText: {
        type: Boolean,
        default: false
      },
      textClass: {
        type: String
      },
      fontSize: {
        type: String,
        default: 'xl',
        validator: v => ['sm', 'md', 'lg', 'xl'].indexOf(v) !== -1
      },
      icon: {
        type: String,
        validator: v => v.startsWith('ph-')
      },
      iconRight: {
        type: String,
        validator: v => {
          return v.startsWith('ph-') || v === 'unset'
        }
      },
      iconSize: {
        type: [String, Number],
        default: 24
      },
      align: {
        type: String,
        default: 'items-center',
        validator: v => ['items-start', 'items-center', 'items-end'].indexOf(v) !== -1
      },
      iconRightSize: {
        type: [String, Number],
        default: 24
      },
      image: {
        type: String
      },
      imageSize: {
        type: [String, Number],
        default: 64
      },
      blurImage: {
        type: Boolean,
        default: false
      },
      blurOptions: {
        type: Object
      },
      imageCircle: {
        type: Boolean,
        default: false
      },
      showDefaultImage: {
        type: Boolean,
        default: false
      },
      imageBranded: {
        type: Boolean,
        default: true
      },
      bold: {
        type: Boolean,
        default: false
      },
      color: {
        type: String,
        default: 'black',
        validator: v =>
          [
            'black',
            'white',
            'concrete',
            'platinum',
            'silver',
            'gray',
            'granite',
            'black',
            'yellow',
            'blue',
            'green',
            'orange',
            'purple',
            'red'
          ].indexOf(v) !== -1
      }
    },
    computed: {
      ...mapGetters(['themeColorHEX']),
      isCard() {
        return this.type === 'card' || this.type === 'card full'
      },
      imageWrapperWidth() {
        return this.isCard ? '' : `${this.imageSize}px`
      },
      imageWrapperHeight() {
        return this.isCard ? '' : `${this.imageSize}px`
      }
    },
    render(h) {
      const wrapper = this.to ? 'router-link' : this.href ? 'a' : 'div'

      return h(
        wrapper,
        {
          ref: this.ref,
          props: this.to && {
            to: this.to
          },
          attrs: this.href && {
            href: this.href,
            target: '_blank',
            rel: 'nofollow noopener'
          },
          nativeOn: this.to && {
            mouseover: () => (this.hovered = true),
            mouseleave: () => (this.hovered = false),
            ...(this.$listeners.clickLink
              ? {
                  click: e => this.$emit('clickLink', e)
                }
              : {})
          },
          ...(!this.to && this.$listeners.clickLink
            ? {
                on: {
                  click: e => this.$emit('clickLink', e)
                }
              }
            : {}),
          class: [
            'flex',
            this.align,
            'w-full',
            'max-w-full',
            'gap-4',
            'min-h-12',
            'transition-colors',
            'duration-200',
            ...(this.isCard ? ['flex-col', 'bg-white', 'p-4', 'shadow-default', 'rounded-lg'] : ['bg-transparent', 'p-2'])
          ]
        },
        [
          ...(this.icon && !this.image && !this.showDefaultImage
            ? [
                h(this.icon, {
                  attrs: {
                    size: this.iconSize
                  },
                  style: {color: this.$helpers.getCorrectColor(this.themeColorHEX)},
                  class: ['shrink-0', 'my-1', 'ml-1']
                })
              ]
            : []),
          ...(this.image || (!this.image && this.showDefaultImage)
            ? [
                h(
                  'div',
                  {
                    style: {
                      backgroundColor: this.imageBranded ? this.themeColorHEX : '',
                      minHeight: this.imageWrapperHeight,
                      height: !this.isCard && this.imageWrapperHeight,
                      minWidth: this.imageWrapperWidth,
                      width: this.imageWrapperWidth
                    },
                    class: [
                      'shrink-0',
                      'flex',
                      'items-center',
                      'justify-center',
                      'overflow-hidden',
                      'max-h-full',
                      ...(this.imageBranded ? [] : ['bg-white']),
                      ...(this.imageCircle && !this.isCard ? ['rounded-full'] : ['rounded']),
                      ...(this.isCard ? ['w-full', 'min-w-full', 'aspect-square'] : [])
                    ]
                  },
                  [
                    ...(this.image && !this.blurImage
                      ? [
                          h('img', {
                            attrs: {
                              src: this.image,
                              alt: this.text
                            },
                            class: ['shrink-0', 'w-full', 'h-full', 'object-cover']
                          })
                        ]
                      : this.image && this.blurImage && this.blurOptions
                      ? [
                          h('t-img', {
                            attrs: {
                              imgSrc: this.image,
                              hash: this.blurOptions.hash,
                              aspectRatio: this.blurOptions.aspectRatio,
                              imgClass: 'shring-0 w-full h-full object-cover'
                            }
                          })
                        ]
                      : []),
                    ...(!this.image
                      ? [
                          h('ph-image', {
                            attrs: {
                              size: !this.isCard ? '44' : '176'
                            },
                            class: ['shrink-0', 'text-white', 'opacity-60']
                          })
                        ]
                      : [])
                  ]
                )
              ]
            : []),
          h(
            'div',
            {
              class: ['flex', 'flex-col', 'items-start', 'w-full', 'max-w-full', 'overflow-hidden']
            },
            [
              h(
                'div',
                {
                  class: ['flex', 'items-center', this.$slots.textRelative ? 'gap-1' : '', this.textInOneLine ? 'max-w-full' : '']
                },
                [
                  ...(this.text
                    ? [
                        h('t-text', {
                          props: {
                            size: this.fontSize,
                            color: this.color
                          },
                          domProps: {
                            innerHTML: this.text
                          },
                          class: [
                            'xs:leading-[1.25rem] mb-[0.125rem]',
                            this.bold ? 'font-semibold' : 'font-normal',
                            this.textInOneLine ? 'truncate' : 'max-w-full',
                            this.textClass
                          ],
                          style: {
                            maxWidth: this.$slots.textRelative ? 'calc(100% - 1rem)' : '100%'
                          }
                        }),
                        this.$slots.textRelative
                      ]
                    : [])
                ]
              ),
              this.$slots.default
            ]
          ),
          ...(this.iconRight && !this.isCard && this.iconRight !== 'unset'
            ? [
                h(this.iconRight, {
                  attrs: {
                    size: this.iconRightSize
                  },
                  style: {color: this.$helpers.getCorrectColor(this.themeColorHEX)},
                  class: ['shrink-0', 'my-1', 'ml-1'],
                  nativeOn: this.$listeners.clickRightIcon
                    ? {
                        click: e => {
                          this.$emit('clickRightIcon', e)
                          e.stopPropagation()
                          e.preventDefault()
                        }
                      }
                    : {}
                })
              ]
            : [])
        ]
      )
    }
  }
</script>
